import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  pdf,
} from "@react-pdf/renderer";
import { Download } from "lucide-react";
import { useCallback } from "react";
import { useMemo, useEffect } from "react";

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 25,
    paddingTop: 70,
    paddingBottom: 30,
    fontFamily: "Times-Roman",
    width: "100dvw",
    color: "#2b323c",
  },
  section: { marginBottom: 10 },
  heading: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
    fontFamily: "Times-Bold",
  },
  paragraph: { fontSize: 12, marginBottom: 6, lineHeight: 1.4 },
  paragraph_2: { fontSize: 12, marginBottom: -2 },
  lineBreak: { marginBottom: 4 },
  list: { marginBottom: 6, paddingLeft: 10 },
  listItem: { fontSize: 12, marginBottom: 2, paddingLeft: 10 },
  orderedListItem: { fontSize: 12, marginBottom: 2, paddingLeft: 10 },
  heading1: { fontSize: 18, fontWeight: "bold", marginBottom: 6 },
  heading2: { fontSize: 16, fontWeight: "bold", marginBottom: 3 },
  heading3: { fontSize: 14, fontWeight: "bold", marginBottom: 6 },
  // Table
  table: {
    display: "table",
    width: "100%",
    marginBottom: 6,
    border: "0.5px solid #777f83",
  },
  //Table Tow
  tableRow: {
    flexDirection: "row",
    width: "100%",
    borderBottom: "0.5px solid #777f83",
  },
  tableRowFirst: {
    flexDirection: "row",
    width: "100%",
    borderBottom: "0.5px solid #777f83",
    fontFamily: "Times-Bold",
    backgroundColor: "#a7b1be",
  },
  tableRowBottom: {
    flexDirection: "row",
    width: "100%",
    fontFamily: "Times-Bold",
  },
  //Table Col
  tableCol: {
    width: "40%",
    padding: 4,
  },
  tableColNum: {
    width: "20%",
    padding: 4,
    borderRight: "0.5px solid #777f83",
  },

  tableCell: { fontSize: 11, color: "#2b323c" },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 20,
    fontSize: 10,
    color: "gray",
  },
  footerLeft: {
    color: "gray",
    fontSize: 10,
  },
  footerRight: {
    color: "gray",
    fontSize: 10,
  },
});

// Parse text content with HTML elements, lists, and tables
const HtmlTextParser = ({ content }) => {
  const elements = [];
  const parser = new DOMParser();

  // Function to handle different content types
  const parseContent = (contentItem, index) => {
    if (contentItem) {
      if (typeof contentItem === "string") {
        const parsedHtml = parser.parseFromString(contentItem, "text/html");

        // Function to traverse HTML nodes
        const traverseNodes = (node) => {
          if (node.nodeName === "#text") {
            const paragraphs = node.textContent.split("\n").filter(Boolean);
            paragraphs.forEach((paragraph, i) => {
              elements.push(
                <Text key={`${index}-p-${i}`} style={styles.paragraph}>
                  {paragraph.trim()}
                </Text>
              );
            });
          } else if (node.nodeName === "P") {
            elements.push(
              <Text key={index} style={styles.paragraph}>
                {node.textContent.trim()}
              </Text>
            );
          } else if (node.nodeName === "Q") {
            elements.push(
              <Text key={index} style={styles.paragraph_2}>
                {node.textContent.trim()}
              </Text>
            );
          } else if (node.nodeName === "BR") {
            elements.push(<Text key={index} style={styles.lineBreak} />);
          } else if (node.nodeName === "H1") {
            elements.push(
              <Text key={index} style={styles.heading1}>
                {node.textContent.trim()}
              </Text>
            );
          } else if (node.nodeName === "H2") {
            elements.push(
              <Text key={index} style={styles.heading2}>
                {node.textContent.trim()}
              </Text>
            );
          } else if (node.nodeName === "H3") {
            elements.push(
              <Text key={index} style={styles.heading3}>
                {node.textContent.trim()}
              </Text>
            );
          } else if (node.nodeName === "UL") {
            elements.push(
              <View key={index} style={styles.list}>
                {Array.from(node.childNodes).map(
                  (liNode, i) =>
                    liNode.nodeName === "LI" && (
                      <Text key={`${index}-ul-li-${i}`} style={styles.listItem}>
                        • {liNode.textContent.trim()}
                      </Text>
                    )
                )}
              </View>
            );
          } else if (node.nodeName === "OL") {
            elements.push(
              <View key={index} style={styles.list}>
                {Array.from(node.childNodes).map(
                  (liNode, i) =>
                    liNode.nodeName === "LI" && (
                      <Text
                        key={`${index}-ol-li-${i}`}
                        style={styles.orderedListItem}
                      >
                        {i + 1}. {liNode.textContent.trim()}
                      </Text>
                    )
                )}
              </View>
            );
          }
        };

        // Traverse parsed HTML nodes
        parsedHtml.body.childNodes.forEach(traverseNodes);
      } else if (Array.isArray(contentItem)) {
        // Handle unordered list (e.g., ['item1', 'item2'])
        elements.push(
          <View key={index} style={styles.list}>
            {contentItem.map((item, i) => (
              <Text key={`${index}-li-${i}`} style={styles.listItem}>
                • {item}
              </Text>
            ))}
          </View>
        );
      } else if (contentItem.type === "orderedList") {
        // Handle ordered list
        elements.push(
          <View key={index} style={styles.list}>
            {contentItem.items.map((item, i) => (
              <Text key={`${index}-oli-${i}`} style={styles.orderedListItem}>
                {i + 1}. {item}
              </Text>
            ))}
          </View>
        );
      } else if (contentItem.type === "table") {
        // Handle table structure
        elements.push(
          <View break key={index} style={styles.table}>
            {contentItem.rows.map((row, rowIndex) => (
              <View
                key={`${index}-tr-${rowIndex}`}
                style={
                  rowIndex === 0
                    ? styles.tableRowFirst
                    : ++rowIndex === contentItem.rows.length
                    ? styles.tableRowBottom
                    : styles.tableRow
                }
              >
                {row.map((cell, cellIndex) => (
                  <View
                    key={`${index}-tc-${cellIndex}`}
                    style={
                      cellIndex === 0 ? styles.tableColNum : styles.tableCol
                    }
                  >
                    <Text style={styles.tableCell}>{cell}</Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        );
      }
    }
  };

  // Parse each content item
  content.forEach((contentItem, index) => parseContent(contentItem, index));

  return <View>{elements}</View>;
};

// Main document component
const MyDocument = ({ data }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {data.map((section, index) => (
        <View key={index} style={styles.section}>
          {section.heading && (
            <Text style={styles.heading}>{section.heading}</Text>
          )}
          {section.content && <HtmlTextParser content={section.content} />}
        </View>
      ))}
      {/* Footer with Dynamic Page Numbers */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>AOS Province</Text>
        <Text
          style={styles.footerRight}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          // fixed // Ensures this footer remains on every page
        />
      </View>
    </Page>
  </Document>
);

const PDFComponent = ({ data, title, setFile = () => {} }) => {
  const memoizedDocument = useMemo(() => <MyDocument data={data} />, [data]);

  const getPDF = useCallback(async () => {
    const blob = await pdf(memoizedDocument).toBlob();
    setFile(new File([blob], `${title}.pdf`, { type: "application/pdf" }));
  }, [memoizedDocument]);
  //

  useEffect(() => {
    getPDF();
  }, [memoizedDocument, data]);

  return (
    <div className="flex">
      <PDFDownloadLink
        document={memoizedDocument}
        fileName={title + ".pdf"}
        className="add-rss-btn inline-block"
      >
        {({ loading }) =>
          loading ? (
            "Loading.."
          ) : (
            <p className="flex gap-2 justify-center items-center py-[3px] sm:py-0">
              <Download size={18} />
              <span className="hidden  sm:inline-block">Download</span>
            </p>
          )
        }
      </PDFDownloadLink>
    </div>
  );
};

export default PDFComponent;
